/**
 * BioperadExplainer — progressive-disclosure explainer for bioperads
 * (sometimes called PROPs in the discrete case).
 *
 * A bioperad equips a single carrier with both operadic composition γ and
 * cooperadic decomposition Δ, tied together by a coherence law. It is the
 * operadic analogue of a bialgebra and the natural setting for CSK's
 * Immunity Closure Guarantee ICG-De: a single envelope that certifies both
 * COMPOSE (build) and DECOMPOSE (extract) safety.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  COMPOSITIONALITY_PRINCIPLE_COLOR,
  PRIMARY,
  THEOREM_COLOR,
} from "../../design-system/tokens.js";
import { MathBlock, MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { BioperadCoherence, ComposeDecomposeAdjunction } from "./operad/index.js";

const ACCENT = PRIMARY[700];
const COMPOSE_ACCENT = THEOREM_COLOR.COMPOSE;
const DECOMPOSE_ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.DECOMPOSE.color;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-bioperad",
  kind: "Categorical structure",
  title: "Bioperad",
  subtitle: "(H, γ : H⊗ⁿ → H, Δ : H → H⊗ᵏ) + coherence",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Operad", href: "#explainer-operad", accent: COMPOSE_ACCENT },
  { label: "Cooperad", href: "#explainer-cooperad", accent: DECOMPOSE_ACCENT },
  { label: "Bialgebra", href: "#explainer-bialgebra" },
  { label: "Bimonad", href: "#explainer-bimonad" },
  { label: "Adjunction", href: "#explainer-adjunction" },
];

function BioperadDiagramStrip(): React.JSX.Element {
  return (
    <div
      style={{
        display: "grid",
        gap: "0.75rem",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
      }}
    >
      <BioperadCoherence accent={ACCENT} />
      <ComposeDecomposeAdjunction
        composeAccent={COMPOSE_ACCENT}
        decomposeAccent={DECOMPOSE_ACCENT}
      />
    </div>
  );
}

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      A <strong>bioperad</strong> is a single carrier that is both an operad (it knows how to
      compose multi-input operations) and a cooperad (it knows how to decompose into multi-output
      parts), with a coherence law tying the two together. CSK uses bioperads to package COMPOSE and
      DECOMPOSE into one auditable Safety Envelope.
    </p>
  ),
  intuition: (
    <>
      <p>
        An operad is the structure of "many-in, one-out." A cooperad is the structure of "one-in,
        many-out." A bioperad has both, on the same carrier, and adds the missing piece:{" "}
        <strong>they have to agree</strong>. Compose first then decompose, and you must get the same
        answer as decomposing first and then composing the parts — possibly after a controlled
        rearrangement (the "shuffle").
      </p>
      <p>
        Think of a complex machine that can be assembled from parts <em>and</em> disassembled into
        parts. A bioperad's coherence law says the disassembly of the assembly is the parts you
        started with, up to a known interleaving. If you can build it, you can take it apart; if you
        can take it apart, you can build it back. Safety is preserved by both directions.
      </p>
      <p>
        For security, this is the structural statement of a <strong>round-trip guarantee</strong>: a
        sub-component you extract from a safe whole, then re-insert, gives you a safe whole again —
        same configuration, same dependencies, no smuggled state.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        A <strong>bioperad</strong> (also called an <em>operad bimodule over itself</em>, or a{" "}
        <em>PROP</em> in the discrete special case) on a carrier <MathInline>{tex`H`}</MathInline>{" "}
        in a symmetric monoidal category <MathInline>{tex`(C, \otimes, I)`}</MathInline> consists
        of:
      </p>
      <ul>
        <li>
          An operad structure: composition{" "}
          <MathInline>{tex`\gamma : H^{\otimes n} \to H`}</MathInline> (informally — packaged as
          operadic substitution{" "}
          <MathInline>
            {tex`\gamma : P(n) \otimes P(k_{1}) \otimes \cdots \otimes P(k_{n}) \to P(k_{1} + \cdots + k_{n})`}
          </MathInline>{" "}
          on the operad <MathInline>{tex`P`}</MathInline> carried by{" "}
          <MathInline>{tex`H`}</MathInline>).
        </li>
        <li>
          A cooperad structure: decomposition{" "}
          <MathInline>{tex`\Delta : H \to H^{\otimes k}`}</MathInline> (analogously{" "}
          <MathInline>
            {tex`\Delta : Q(k) \to \bigoplus P(n) \otimes Q(k_{1}) \otimes \cdots \otimes Q(k_{n})`}
          </MathInline>{" "}
          on the cooperad <MathInline>{tex`Q`}</MathInline>).
        </li>
        <li>
          A <strong>compatibility coherence</strong> tying the two:
          <MathBlock ariaLabel="Δ ∘ γ ≡ (γ ⊗ γ) ∘ shuffle ∘ (Δ ⊗ Δ)">
            {tex`\Delta \circ \gamma \;\equiv\; (\gamma \otimes \gamma) \circ \mathrm{shuffle} \circ (\Delta \otimes \Delta)`}
          </MathBlock>
          Compose-then-decompose factors through decompose-then-compose after a fixed Σ-equivariant
          rearrangement.
        </li>
      </ul>
      <p>
        Equivalently: <MathInline>{tex`\gamma`}</MathInline> is a cooperad morphism with respect to{" "}
        <MathInline>{tex`\Delta`}</MathInline>, and <MathInline>{tex`\Delta`}</MathInline> is an
        operad morphism with respect to <MathInline>{tex`\gamma`}</MathInline>. The square commutes
        up to the shuffle.
      </p>
      <p>
        A <strong>Hopf operad</strong> is a bioperad with an antipode; the COMPOSE/DECOMPOSE pair in
        CSK lifts to a Hopf operad in the symmetric monoidal category in which CSK's safety
        predicate is monoidal.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        The bioperad packages CSK's two operadic SSMs — COMPOSE and DECOMPOSE — into one record and
        one coherence law. That is exactly the structure of the{" "}
        <strong>Immunity Closure Guarantee ICG-De</strong>, also called the <em>Safety Envelope</em>{" "}
        for the COMPOSE/DECOMPOSE pair.
      </p>
      <ul>
        <li>
          <strong>One certificate for two directions.</strong> Instead of separately auditing "safe
          composition" and "safe extraction," ICG-De carries the unit/counit of the{" "}
          <MathInline>{tex`\text{operad} \dashv \text{cooperad}`}</MathInline> adjunction and the
          bioperad coherence square. One envelope. One audit.
        </li>
        <li>
          <strong>Round-trip safety.</strong> The coherence law refuses to admit a "build" that has
          no matching "decompose," or a "decompose" that fails to recompose into the original. A
          system that satisfies the bioperad coherence is round-trip safe by construction.
        </li>
        <li>
          <strong>17 → 7.</strong> ICG-De is one of the seven Safety Envelopes that compress the
          full SSM audit surface from 17 individual checks (10 Neutralization Theorems + 7
          Compositionality Principles) down to 7 envelopes; the bioperad coherence is the structural
          reason the compression is sound.
        </li>
      </ul>
      <p>
        For software, the bioperad's compose/decompose coherence rules out the entire family of
        attacks where a "safe disassembly" is replayed in a different context to reassemble a
        capability the original tree could not have produced. The shuffle is fixed; the residual
        dependencies are mandatory; the cell witness lives on the operadic side.
      </p>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — ICG-De Safety Envelope",
      path: "docs/reference/Theorem-Canon.md",
      note: "§7a Immunity Closure Guarantees: COMPOSE (C) + DECOMPOSE (De) as one envelope.",
    },
    {
      label: "Composition governance witnesses (Agda)",
      path: "agda/Foundation/Governance/CompositionGovernanceWitnesses.agda",
      note: "Operad + cooperad evidence records used by the bioperad-level governance layer.",
    },
    {
      label: "Operad coherence (Agda)",
      path: "agda/Foundation/CompositionPrinciples/OperadCoherence.agda",
      note: "Associahedra-indexed coherence underlying the bioperad's γ side.",
    },
    {
      label: "Bialgebra explainer",
      path: "demos/ui/src/explainers/categorical/BialgebraExplainer.tsx",
      note: "Algebra/coalgebra analogue of the bioperad's γ/Δ pair.",
    },
    {
      label: "Markl, Shnider & Stasheff — Operads in Algebra, Topology and Physics",
      path: "https://bookstore.ams.org/surv-96",
      note: "Standard external reference for bioperads, PROPs, and Hopf operads.",
    },
  ],
};

export function BioperadExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<BioperadDiagramStrip />}
      crossLinks={CROSS_LINKS}
    />
  );
}
